import { Checkbox } from "@mui/material";
import { Menu } from "components";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "components/atoms/BasicTable";
import PulseListSkeleton from "components/atoms/layout/PulseListSkeleton/PulseListSkeleton";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { getClientICP } from "store/slices/icp.slice";
import constants from "utils/constants";
import { useTable } from "utils/hooks/useTable";
import { bodyContainer } from "../../style";
import { PulseRow } from "../row";
import { getContactRevealedByID } from "store/slices/data.slice";

export const TableDatabase = () => {
  const {
    isLoadingData: isLoading,
    leadsOnPage: leads,
    count,
  } = useAppSelector((state) => state.searchMain);
  const { exportOpen, filterOpen } = useAppSelector((state) => state.ui);
  const {
    selectedRows,
    unselectedIds,
    isAllSelected,
    handleSelectRow,
    handleSelectAll,
    handleSelectAllInPage,
    handleUnselectRow,
    handleDeselectAll,
  } = useTable({ totalItems: count });
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);
  const { container } = bodyContainer({
    isExport: exportOpen,
    sidebarOpen: filterOpen,
  });
  const dispatch = useAppDispatch();
  const [searchParams, _] = useSearchParams();
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowContent(true), 200);
      return () => clearTimeout(timeout);
    } else {
      setShowContent(false);
    }
  }, [isLoading]);

  useEffect(() => {
    dispatch(getClientICP());
  }, [dispatch]);

  return (
    <>
      {isLoading && (
        <div className="opacity-100 transition-opacity duration-300">
          <PulseListSkeleton removeHeader />
        </div>
      )}

      {!isLoading && (
        <div
          className={`${container()} ${
            showContent ? "opacity-100" : "opacity-0"
          } transition-opacity duration-300`}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableCell
                  isHeader
                  classNameComponent="px-0 max-w-[20px]"
                  className="w-1 max-w-[20px] !p-0"
                >
                  <Checkbox
                    onClick={(event) => {
                      event.stopPropagation();
                      if (isAllSelected) {
                        handleDeselectAll();
                        return;
                      }
                      setAnchor(event.currentTarget);
                    }}
                    checked={isAllSelected}
                    sx={{
                      border: "var(--colo-gray-700)",
                      color: "var(--color-gray-600)",
                      "&.Mui-checked": { color: "var(--color-primary-500)" },
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  />
                </TableCell>
                <TableCell
                  className="text-left !font-semibold !text-white-500"
                  isHeader
                >
                  Name
                </TableCell>
                <TableCell
                  className="text-left !font-semibold !text-white-500"
                  isHeader
                >
                  Title
                </TableCell>
                <TableCell
                  className="text-left !font-semibold !text-white-500"
                  isHeader
                >
                  Company
                </TableCell>
                <TableCell
                  className="text-left max-w-[40px] !font-semibold !text-white-500"
                  isHeader
                >
                  Email
                </TableCell>
                <TableCell
                  className="text-left !font-semibold !text-white-500"
                  isHeader
                >
                  Phone
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads?.map((row) => (
                <PulseRow
                  key={row.leadId}
                  id={row.leadId}
                  currentParams={searchParams.toString()}
                  selectedRows={selectedRows}
                  unselectedIds={unselectedIds}
                  isAllSelected={isAllSelected}
                  handleSelectRow={handleSelectRow}
                  handleUnselectRow={handleUnselectRow}
                  {...row}
                  handleRevealContactByID={() =>
                    dispatch(
                      getContactRevealedByID({ leadId: row.leadId })
                    ).unwrap()
                  }
                />
              ))}
            </TableBody>
          </Table>
          <Menu
            items={[
              {
                title: "Select this page",
                onClick: () =>
                  handleSelectAllInPage(leads.map((item) => item.leadId)),
              },
              {
                title: `Select all people (${
                  count < constants.LIMIT_ENRICHMENT_ATTEMPT_PER_TASK
                    ? count
                    : constants.LIMIT_ENRICHMENT_ATTEMPT_PER_TASK.toLocaleString()
                })`,
                onClick: () => handleSelectAll(),
              },
            ]}
            isOpen={open}
            anchor={anchor}
            setAnchor={setAnchor}
            position="left"
          />
        </div>
      )}
    </>
  );
};
